import Image from 'next/image';
import { Order } from '@/lib/shopify/types';
import { formatPrice } from '@/lib/shopify';

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function statusLabel(raw: string | null | undefined): { label: string; tone: 'pending' | 'partial' | 'done' | 'muted' } {
  if (!raw) return { label: 'Pending', tone: 'pending' };
  const s = raw.toUpperCase();
  if (s === 'FULFILLED') return { label: 'Fulfilled', tone: 'done' };
  if (s === 'PARTIALLY_FULFILLED') return { label: 'Partially fulfilled', tone: 'partial' };
  if (s === 'UNFULFILLED') return { label: 'Processing', tone: 'pending' };
  if (s === 'SCHEDULED') return { label: 'Scheduled', tone: 'muted' };
  if (s === 'ON_HOLD') return { label: 'On hold', tone: 'muted' };
  return { label: raw.replace(/_/g, ' ').toLowerCase(), tone: 'muted' };
}

export default function OrderList({ orders }: { orders: Order[] }) {
  if (orders.length === 0) {
    return (
      <div className="orders-empty">
        <p>You haven&apos;t placed an order yet.</p>
      </div>
    );
  }

  return (
    <ul className="order-list">
      {orders.map((order) => {
        const status = statusLabel(order.fulfillmentStatus);
        return (
          <li key={order.id} className="order-card">
            <header className="order-head">
              <div>
                <span className="order-number">{order.name}</span>
                <span className="order-date">{formatDate(order.processedAt)}</span>
              </div>
              <span className={`order-status order-status-${status.tone}`}>
                {status.label}
              </span>
            </header>

            <div className="order-items">
              {order.lineItems.slice(0, 4).map((item, idx) => (
                <div key={idx} className="order-item">
                  {item.variant?.image?.url ? (
                    <Image
                      src={item.variant.image.url}
                      alt={item.variant.image.altText || item.title}
                      width={56}
                      height={56}
                      className="order-item-img"
                    />
                  ) : (
                    <div className="order-item-img order-item-img-ph" />
                  )}
                  <div className="order-item-body">
                    <p className="order-item-title">{item.title}</p>
                    {item.variant?.title && item.variant.title !== 'Default Title' && (
                      <p className="order-item-variant">{item.variant.title}</p>
                    )}
                    <p className="order-item-qty">Qty: {item.quantity}</p>
                  </div>
                </div>
              ))}
              {order.lineItems.length > 4 && (
                <p className="order-item-more">+ {order.lineItems.length - 4} more</p>
              )}
            </div>

            <footer className="order-foot">
              <div>
                <span className="order-foot-label">Total</span>
                <span className="order-total">{formatPrice(order.currentTotalPrice)}</span>
              </div>
              <a
                href={order.statusUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="order-track-btn"
              >
                Track order →
              </a>
            </footer>
          </li>
        );
      })}

      <style>{`
        .order-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .orders-empty {
          padding: 2.5rem 1rem;
          text-align: center;
          background: var(--color-off-white);
          border: 1px dashed var(--color-border);
          border-radius: 0.75rem;
          color: var(--color-text-secondary);
        }
        .order-card {
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          padding: 1rem 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 0.9rem;
        }
        .order-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid var(--color-border);
        }
        .order-number {
          display: block;
          font-weight: 700;
          font-size: 1rem;
          color: var(--color-text-primary);
        }
        .order-date {
          display: block;
          font-size: 0.8rem;
          color: var(--color-text-muted);
          margin-top: 0.1rem;
        }
        .order-status {
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.3rem 0.6rem;
          border-radius: 999px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          white-space: nowrap;
        }
        .order-status-done { background: #eaf5ee; color: #1b5a30; }
        .order-status-partial { background: #fff4e1; color: #8a5200; }
        .order-status-pending { background: #e9eef5; color: #2a4a73; }
        .order-status-muted { background: var(--color-off-white); color: var(--color-text-secondary); }
        .order-items {
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }
        .order-item {
          display: grid;
          grid-template-columns: 56px 1fr;
          gap: 0.8rem;
          align-items: center;
        }
        .order-item-img {
          border-radius: 0.5rem;
          object-fit: contain;
          padding: 0.2rem;
          box-sizing: border-box;
          background: var(--color-off-white);
        }
        .order-item-img-ph {
          width: 56px;
          height: 56px;
        }
        .order-item-body { min-width: 0; }
        .order-item-title {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .order-item-variant {
          font-size: 0.75rem;
          color: var(--color-text-muted);
          margin: 0.1rem 0 0;
        }
        .order-item-qty {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
          margin: 0.1rem 0 0;
        }
        .order-item-more {
          font-size: 0.8rem;
          color: var(--color-text-muted);
          margin: 0.3rem 0 0 4.3rem;
        }
        .order-foot {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid var(--color-border);
        }
        .order-foot-label {
          display: block;
          font-size: 0.75rem;
          color: var(--color-text-muted);
        }
        .order-total {
          font-weight: 700;
          font-size: 1.05rem;
          color: var(--color-primary);
        }
        .order-track-btn {
          background: var(--color-primary);
          color: var(--color-white);
          padding: 0.6rem 1rem;
          border-radius: 1rem;
          font-size: 0.85rem;
          font-weight: 600;
          text-decoration: none;
          transition: background 0.15s;
          white-space: nowrap;
        }
        .order-track-btn:hover { background: var(--color-primary-dark); }
      `}</style>
    </ul>
  );
}
